import { nextTick, reactive } from 'vue'
import {
  openPagePortal,
  preparePagePortal,
  type PortalFlight,
} from './page-portal'
import type {
  Router,
  RouteLocationNormalized,
  RouterScrollBehavior,
} from 'vue-router'

type Kind =
  | 'forward'
  | 'back'
  | 'ascend'
  | 'descend'
  | 'ticket-open'
  | 'ticket-close'
  | 'auth'
  | 'departure'
interface NativeTransition {
  ready: Promise<void>
  finished: Promise<void>
  skipTransition(): void
}
interface Journey {
  id: number
  to: string
  kind: Kind
  direction: number
  releaseGuard: () => void
  releaseCapture: () => void
  captureReady: Promise<void>
  transition?: NativeTransition
  portal?: PortalFlight
  timer?: ReturnType<typeof setTimeout>
  animations: Animation[]
  shared: HTMLElement[]
  sharedId?: string
  cancelled: boolean
}

const media = window.matchMedia('(prefers-reduced-motion: reduce)')
let stored = true
try {
  stored = localStorage.getItem('cqu-page-motion') !== 'off'
} catch {}
export const pageMotion = reactive({
  enabled: stored,
  reduced: media.matches,
  busy: false,
  announcement: '',
})
export const canAnimate = () =>
  pageMotion.enabled && !pageMotion.reduced && !document.hidden
let active: Journey | undefined
let sequence = 0
let popDirection = 0
let clickedTicket: HTMLElement | null = null
let clickedPortal: HTMLElement | null = null

function applyPreference() {
  document.documentElement.dataset.pageMotion =
    pageMotion.enabled && !pageMotion.reduced ? 'on' : 'off'
  if (!canAnimate()) cancelJourney()
}
export function togglePageMotion() {
  pageMotion.enabled = !pageMotion.enabled
  try {
    localStorage.setItem('cqu-page-motion', pageMotion.enabled ? 'on' : 'off')
  } catch {}
  applyPreference()
}
function reducedChanged() {
  pageMotion.reduced = media.matches
  applyPreference()
}
media.addEventListener('change', reducedChanged)
applyPreference()

const labels: Record<string, string> = {
  StudentHome: '活动中心',
  MyRegistrations: '我的报名',
  MyScore: '我的积分',
  ActivityDetail: '活动详情',
  Login: '钉钉登录',
  AdminLogin: '老师登录',
  StudentAdminHome: '活动管理',
  AdminHome: '系统管理',
  CreateActivity: '创建活动',
  EditActivity: '编辑活动',
  ActivityRegistrations: '报名管理',
  NotFound: '页面未找到',
}
function rank(route: RouteLocationNormalized) {
  return (
    (
      {
        StudentHome: 0,
        MyRegistrations: 1,
        MyScore: 2,
        StudentAdminHome: 3,
        AdminHome: 3,
      } as Record<string, number>
    )[String(route.name)] ?? 4
  )
}
function classify(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): [Kind, number] {
  const direction = popDirection || (rank(to) >= rank(from) ? 1 : -1)
  if (to.meta.requiresAuth === false && from.meta.requiresAuth === false)
    return ['auth', to.name === 'AdminLogin' ? 1 : -1]
  if (from.meta.requiresAuth === false || to.meta.requiresAuth === false)
    return ['departure', direction]
  if (to.name === 'ActivityDetail') return ['ticket-open', 1]
  if (from.name === 'ActivityDetail') return ['ticket-close', -1]
  if (to.name === 'MyScore') return ['ascend', direction]
  if (from.name === 'MyScore' && direction < 0) return ['descend', direction]
  return [direction < 0 ? 'back' : 'forward', direction]
}
function clearShared(journey: Journey) {
  for (const element of journey.shared)
    element.style.removeProperty('view-transition-name')
  journey.shared = []
}
function finishJourney(journey: Journey) {
  clearTimeout(journey.timer)
  clearShared(journey)
  if (active !== journey) return
  pageMotion.busy = false
  document.documentElement.removeAttribute('data-route-transition')
  active = undefined
}
function cancelJourney() {
  if (!active) return
  const journey = active
  journey.cancelled = true
  journey.releaseGuard()
  journey.releaseCapture()
  journey.transition?.skipTransition()
  journey.portal?.cancel()
  for (const animation of journey.animations) animation.cancel()
  finishJourney(journey)
}
function isVisible(element: HTMLElement | null): element is HTMLElement {
  if (!element) return false
  const rect = element.getBoundingClientRect()
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.top < innerHeight
  )
}
function share(element: HTMLElement, journey: Journey) {
  element.style.setProperty('view-transition-name', 'activity-focus')
  journey.shared.push(element)
}
function ticketFor(id: string) {
  // IDs come from route params, never inserted into a CSS selector.
  return (
    Array.from(document.querySelectorAll<HTMLElement>('[data-ticket-id]')).find(
      (element) => element.dataset.ticketId === id && isVisible(element),
    ) || null
  )
}
function prepareOrigin(
  journey: Journey,
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) {
  if (journey.kind === 'ticket-open') {
    const id = String(to.params.id)
    const origin =
      clickedTicket?.dataset.ticketId === id && isVisible(clickedTicket)
        ? clickedTicket
        : ticketFor(id)
    if (origin) {
      share(origin, journey)
      journey.sharedId = id
    }
  } else if (journey.kind === 'ticket-close') {
    const origin = document.querySelector<HTMLElement>('[data-activity-focus]')
    if (isVisible(origin)) {
      share(origin, journey)
      journey.sharedId = String(from.params.id)
    }
  }
  clickedTicket = null
}
function suppressDoubleEntrance() {
  // A captured page must not contain half-transparent CSS entrance animations.
  // Leave this inline override on these mounted nodes: removing it would restart them.
  document.querySelectorAll<HTMLElement>('.anim-rise').forEach((element) => {
    element.style.animationName = 'none'
  })
}

/** Bound the wait for async page data. Slow APIs still reveal their loading state. */
function waitForContent(maxWait = 180): Promise<void> {
  if (!document.querySelector('[data-route-pending]')) return Promise.resolve()
  return new Promise((resolve) => {
    const stop = () => {
      observer.disconnect()
      clearTimeout(timer)
      resolve()
    }
    const observer = new MutationObserver(() => {
      if (!document.querySelector('[data-route-pending]')) stop()
    })
    const timer = setTimeout(stop, maxWait)
    observer.observe(document.getElementById('app')!, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-route-pending'],
    })
  })
}
function fallback(journey: Journey) {
  if (active !== journey || journey.cancelled || !canAnimate()) {
    finishJourney(journey)
    return
  }
  const html = document.documentElement
  html.dataset.routeEngine = 'fallback'
  const content =
    document.querySelector<HTMLElement>('.auth-panel') ||
    document.querySelector<HTMLElement>('.app-main') ||
    document.querySelector<HTMLElement>('#app > div')
  if (!content?.animate) {
    finishJourney(journey)
    return
  }
  const vertical = journey.kind === 'ascend' || journey.kind === 'descend'
  const ticket = journey.kind.startsWith('ticket')
  const x = vertical || ticket ? 0 : 34 * journey.direction
  const y =
    journey.kind === 'ascend'
      ? 30
      : journey.kind === 'descend'
        ? -22
        : ticket
          ? 18
          : 0
  const animation = content.animate(
    [
      {
        opacity: 0,
        transform: `translate3d(${x}px, ${y}px, 0) scale(${ticket ? 0.975 : 0.994})`,
      },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
    ],
    { duration: 380, easing: 'cubic-bezier(.16, 1, .3, 1)' },
  )
  journey.animations.push(animation)
  animation.finished.catch(() => {}).then(() => finishJourney(journey))
}

/** Vue Router calls this after committing the new component. It also releases
 * the native snapshot gate, so old/new screenshots use the right scroll position. */
export const motionScrollBehavior: RouterScrollBehavior = async (
  to,
  _from,
  savedPosition,
): Promise<false> => {
  const expected = to.fullPath
  const generation = sequence
  await nextTick()
  await waitForContent()
  if (generation !== sequence) return false
  const journey = active?.to === expected ? active : undefined
  const position = savedPosition || { top: 0, left: 0 }
  if (journey?.portal) {
    suppressDoubleEntrance()
    pageMotion.announcement = `已进入${labels[String(to.name)] || '新页面'}`
    journey.portal.reveal(position)
    return false
  }
  window.scrollTo({
    top: position.top,
    left: position.left,
    behavior: 'instant',
  })
  if (journey) {
    suppressDoubleEntrance()
    if (journey.sharedId) {
      const target =
        journey.kind === 'ticket-open'
          ? document.querySelector<HTMLElement>('[data-activity-focus]')
          : ticketFor(journey.sharedId)
      if (target) share(target, journey)
    }
    pageMotion.announcement = `已进入${labels[String(to.name)] || '新页面'}`
    journey.releaseCapture()
    if (!journey.transition) fallback(journey)
  }
  return false
}

export function installRouteMotion(router: Router) {
  const removeHistoryListener = router.options.history.listen(
    (_to, _from, info) => {
      popDirection = info.delta < 0 ? -1 : 1
    },
  )
  const captureTicket = (event: MouseEvent) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return
    clickedTicket = (event.target as Element).closest<HTMLElement>(
      '[data-ticket-id]',
    )
    clickedPortal = (event.target as Element).closest<HTMLElement>(
      '[data-route-portal]',
    )
  }
  const visibilityChanged = () => {
    if (document.hidden) cancelJourney()
  }
  document.addEventListener('click', captureTicket, true)
  document.addEventListener('visibilitychange', visibilityChanged)

  const removeBefore = router.beforeEach((to, from) => {
    cancelJourney()
    sequence++
    if (!from.matched.length || to.path === from.path || !canAnimate()) {
      clickedPortal = null
      clickedTicket = null
      popDirection = 0
      document.documentElement.dataset.routeEngine = 'off'
      return
    }
    const [kind, direction] = classify(to, from)
    popDirection = 0
    let releaseCapture!: () => void
    const captureReady = new Promise<void>((resolve) => {
      releaseCapture = resolve
    })
    active = {
      id: sequence,
      to: to.fullPath,
      kind,
      direction,
      releaseGuard: () => {},
      releaseCapture,
      captureReady,
      animations: [],
      shared: [],
      cancelled: false,
    }
    pageMotion.busy = true
    const html = document.documentElement
    html.dataset.routeKind = kind
    html.dataset.routeDirection = String(direction)
    html.dataset.routeTransition = 'pending'
  })

  const removeResolve = router.beforeResolve(async (to, from) => {
    const journey = active
    if (!journey || journey.to !== to.fullPath || !canAnimate()) return
    const origin =
      clickedPortal?.dataset.routePortal === to.path
        ? clickedPortal
        : to.name === 'ActivityDetail' &&
            clickedTicket?.dataset.ticketId === String(to.params.id)
          ? clickedTicket
          : null
    clickedPortal = null
    if (isVisible(origin)) {
      clickedTicket = null
      // Await the identical animation engine; do not replace the transition.
      // If navigation changed while downloading, the stale flight must not open.
      await preparePagePortal()
      if (active !== journey || journey.cancelled || !canAnimate()) return
      return new Promise<void>((resolve) => {
        journey.releaseGuard = resolve
        document.documentElement.dataset.routeEngine = 'gsap-flip'
        document.documentElement.dataset.routeTransition = 'running'
        try {
          journey.portal = openPagePortal(
            origin,
            to.path,
            labels[String(to.name)] || '校园探索',
            resolve,
            () => finishJourney(journey),
          )
          if (!journey.portal) resolve()
          journey.timer = setTimeout(() => {
            journey.portal?.cancel()
            resolve()
            finishJourney(journey)
          }, 1800)
        } catch {
          resolve()
        }
      })
    }
    const start = (
      document as Document & {
        startViewTransition?: (
          callback: () => Promise<void>,
        ) => NativeTransition
      }
    ).startViewTransition
    if (!start) {
      document.documentElement.dataset.routeEngine = 'fallback'
      return
    }
    return new Promise<void>((resolve) => {
      journey.releaseGuard = resolve
      prepareOrigin(journey, to, from)
      suppressDoubleEntrance()
      document.documentElement.dataset.routeEngine = 'native'
      document.documentElement.dataset.routeTransition = 'running'
      try {
        journey.transition = start.call(document, async () => {
          resolve()
          await journey.captureReady
        })
        const native = journey.transition
        // Handle rejection on both promises: skipTransition() is normal on rapid navigation.
        native.finished.catch(() => {})
        native.ready.then(
          () => {
            native.finished.catch(() => {}).then(() => finishJourney(journey))
          },
          async () => {
            await journey.captureReady
            if (active === journey && !journey.cancelled) fallback(journey)
          },
        )
        // A rendering/visibility quirk must never hold a navigation hostage.
        journey.timer = setTimeout(() => {
          resolve()
          journey.releaseCapture()
          journey.transition?.skipTransition()
          finishJourney(journey)
        }, 1200)
      } catch {
        clearShared(journey)
        journey.transition = undefined
        resolve()
      }
    })
  })
  const removeAfter = router.afterEach((to, _from, failure) => {
    if (failure && active?.to === to.fullPath) cancelJourney()
    if (!failure && active?.to === to.fullPath) active.portal?.mount()
    if (!failure)
      document.title = `${labels[String(to.name)] || '校园探索'} · CQU 活动积分`
  })
  const removeError = router.onError(() => cancelJourney())
  if (import.meta.hot)
    import.meta.hot.dispose(() => {
      cancelJourney()
      removeHistoryListener()
      removeBefore()
      removeResolve()
      removeAfter()
      removeError()
      document.removeEventListener('click', captureTicket, true)
      document.removeEventListener('visibilitychange', visibilityChanged)
      media.removeEventListener('change', reducedChanged)
    })
}
